import { useState } from "react";
import {
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Grid2 as Grid,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import { Link, useNavigate, useParams } from "react-router-dom";
import { skipToken } from "@reduxjs/toolkit/dist/query";
import {
  resolveSurveyStatus,
  useCreateSurveyMutation,
  useFetchSurveysQuery,
} from "@/api/surveysApi";
import { EmptyState } from "@/components";
import AvailableQuota from "@/components/AvailableQuota";
import PageHeader from "@/components/PageHeader";
import ROUTES from "@/constants/routes";
import { useAppSelector } from "@/hooks";
import useOrganizationPlan from "@/hooks/useOrganizationPlan";
import useSnackbar from "@/hooks/useSnackbar";
import { NewSurvey, Survey, SurveyStatus } from "@/types/surveys";
import { HugeiconsIcon } from "@hugeicons/react";
import { BubbleChatFavouriteIcon as ChatFavouriteIcon } from "@hugeicons/core-free-icons";
import CreateSurveyDialog from "./components/CreateSurveyDialog";

const STATUS_CHIP_CONFIG: Record<
  SurveyStatus,
  {
    label: string;
    color: "success" | "warning" | "default" | "error";
  }
> = {
  active: { label: "Activa", color: "success" },
  draft: { label: "Borrador", color: "default" },
  paused: { label: "Pausada", color: "warning" },
  finished: { label: "Finalizada", color: "error" },
};

const SurveysPage = () => {
  const navigate = useNavigate();
  const [openSnackbar] = useSnackbar();
  const { organizationId } = useParams<{ organizationId: string }>();
  const container = useAppSelector((state) => state.ui?.container);
  const { checkFeatureLimit, openUpgradePopUp } = useOrganizationPlan("survey");

  const [isCreateOpen, setIsCreateOpen] = useState(false);

  const surveysQuery = useFetchSurveysQuery(organizationId || skipToken);
  const [createSurvey, createState] = useCreateSurveyMutation();

  const handleCreateClick = () => {
    if (checkFeatureLimit(surveysList.length)) {
      setIsCreateOpen(true);
    } else {
      openUpgradePopUp();
    }
  };

  const handleCreateSurvey = async (draft: Partial<NewSurvey>) => {
    if (!organizationId) return;
    if (!checkFeatureLimit(surveysList.length)) {
      openUpgradePopUp();
      return;
    }
    try {
      const result = await createSurvey({
        organizationId,
        payload: draft,
      }).unwrap();

      setIsCreateOpen(false);
      openSnackbar("Encuesta creada en borrador", "success");
      navigate(`/comercios/${organizationId}/surveys/${result.id}/edit`);
    } catch {
      openSnackbar("Error al crear la encuesta", "error");
    }
  };

  const surveysList = surveysQuery.data ?? [];

  return (
    <Stack spacing={3}>
      <PageHeader
        title="Encuestas"
        action={
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2} justifyContent="center" alignItems="center">
            <AvailableQuota feature="survey" usedQuota={surveysList.length} />
            <Button
              size="large"
              color="success"
              variant="contained"
              onClick={handleCreateClick}
            >
              Crear Encuesta
            </Button>
          </Stack>
        }
      />

      <Stack flex={1}>
        <Container
          maxWidth={container === "fixed" ? "lg" : false}
          sx={{ height: "100%", pb: 5 }}
        >
          {surveysQuery.isLoading ? (
            <Stack flex={1} alignItems="center" py={5}>
              <CircularProgress />
            </Stack>
          ) : surveysList.length === 0 ? (
            <Paper sx={{ p: 4 }}>
              <EmptyState
                title="Sin Encuestas"
                description="Crea tu primera encuesta para conocer la satisfacción de tus clientes después de cada pedido o en tu local."
                action={{
                  children: "Crear Encuesta",
                  onClick: handleCreateClick,
                }}
              />
            </Paper>
          ) : (
            <Grid container spacing={3} alignItems="flex-start">
              {surveysList.map((survey: Survey) => {
                const totalCount = survey.totalResponses ?? survey.answers ?? 0;
                const status = resolveSurveyStatus(survey);
                const chipConfig = STATUS_CHIP_CONFIG[status] ?? STATUS_CHIP_CONFIG.draft;

                return (
                  <Grid key={survey.id} size={{ xs: 12, sm: 6, lg: 4 }}>
                    <Card
                      sx={{
                        borderRadius: 3,
                        overflow: "hidden",
                      }}
                    >
                      {/* Card Header con fondo azul de la marca y padding de 24px (16px inferior) */}
                      <Box
                        sx={{
                          bgcolor: "#06175D",
                          color: "common.white",
                          pt: "24px",
                          px: "24px",
                          pb: "16px",
                        }}
                      >
                        <Stack spacing={5}>
                          <Stack
                            direction="row"
                            justifyContent="space-between"
                            alignItems="center"
                          >
                            <Chip
                              size="small"
                              color={chipConfig.color}
                              label={chipConfig.label}
                              sx={
                                status === "draft"
                                  ? {
                                    bgcolor: "rgba(255, 255, 255, 0.2)",
                                    color: "common.white",
                                  }
                                  : undefined
                              }
                            />

                            <Stack direction="row" spacing={0.75} alignItems="center">
                              <HugeiconsIcon icon={ChatFavouriteIcon} size={18} color="#ffffff" />
                              <Typography
                                variant="subtitle2"
                                sx={{ color: "common.white", fontWeight: 600 }}
                              >
                                {totalCount}
                              </Typography>
                            </Stack>
                          </Stack>

                          <Typography
                            variant="h3"
                            sx={{
                              color: "common.white",
                            }}
                          >
                            {survey.title}
                          </Typography>
                        </Stack>
                      </Box>

                      {/* Cuerpo de la tarjeta y acciones con padding de 24px (16px superior) */}
                      <Stack sx={{ pt: "16px", px: "24px", pb: "24px" }} spacing={3}>
                        <CardContent sx={{ p: 0, "&:last-child": { pb: 0 } }}>
                          <Typography variant="body2">
                            {survey.description || "Sin descripción configurada."}
                          </Typography>
                        </CardContent>

                        <CardActions sx={{ p: 0, justifyContent: "flex-start" }}>
                          <Button
                            variant="contained"
                            color="primary"
                            component={Link}
                            to={ROUTES.organizations.surveys.details
                              .replace(":organizationId", organizationId ?? "")
                              .replace(":surveyId", survey.id ?? "")}
                          >
                            Ver
                          </Button>
                        </CardActions>
                      </Stack>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          )}
        </Container>
      </Stack>

      <CreateSurveyDialog
        open={isCreateOpen}
        onClose={() => setIsCreateOpen(false)}
        isLoading={createState.isLoading}
        onSelectTemplate={handleCreateSurvey}
      />
    </Stack>
  );
};

export default SurveysPage;
